import { Injectable } from '@angular/core';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import { whatsappPhones as whatsappPhonesData } from 'app/mock-api/common/whatsapp/data';
import { cloneDeep } from 'lodash-es';

@Injectable({ providedIn: 'root' })
export class WhatsAppMockApi {
    private _phones: any[] = whatsappPhonesData;

    /**
     * Constructor
     */
    constructor(private _fuseMockApiService: FuseMockApiService) {
        // Register Mock API handlers
        this.registerHandlers();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ WhatsApp phones - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/whatsapp/phones')
            .reply(() => [200, cloneDeep(this._phones)]);

        // -----------------------------------------------------------------------------------------------------
        // @ Configure webhook - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/whatsapp/:phoneNumberId/webhook')
            .reply(({ request, urlParams }) => {
                const phoneNumberId = urlParams.phoneNumberId;
                const { url } = request.body;

                // Find the phone
                const phoneIndex = this._phones.findIndex(phone => phone.phoneNumberId === phoneNumberId);
                
                if (phoneIndex === -1) {
                    return [404, { error: 'Phone not found' }];
                }

                // Update the webhook URL
                this._phones[phoneIndex] = {
                    ...this._phones[phoneIndex],
                    metadata: {
                        ...this._phones[phoneIndex].metadata,
                        webhook: url
                    }
                };

                // Return the updated phone
                return [200, { ...cloneDeep(this._phones[phoneIndex]), secretHint: null }];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Get webhook - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/whatsapp/:phoneNumberId/webhook')
            .reply(({ urlParams }) => {
                const phoneNumberId = urlParams.phoneNumberId;

                // Find the phone
                const phone = this._phones.find(p => p.phoneNumberId === phoneNumberId);
                
                if (!phone) {
                    return [404, { error: 'Phone not found' }];
                }

                // Return the webhook URL
                return [200, { url: phone.metadata?.webhook || '', secretHint: null }];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Register phone - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/whatsapp/:phoneNumberId/register')
            .reply(({ urlParams }) => {
                const phoneNumberId = urlParams.phoneNumberId;

                // Find the phone
                const phoneIndex = this._phones.findIndex(phone => phone.phoneNumberId === phoneNumberId);
                
                if (phoneIndex === -1) {
                    return [404, { error: 'Phone not found' }];
                }

                // Update the phone status
                this._phones[phoneIndex] = {
                    ...this._phones[phoneIndex],
                    metadata: {
                        ...this._phones[phoneIndex].metadata,
                        phoneNumberStatus: 'VERIFIED'
                    }
                };

                // Return the updated phone
                return [200, cloneDeep(this._phones[phoneIndex])];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Get connection URL - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/whatsapp/connection-url')
            .reply(() => {
                // Return a mock WhatsApp connection URL
                return [200, {
                    url: 'https://www.facebook.com/v18.0/dialog/oauth?client_id=YOUR_APP_ID&redirect_uri=YOUR_REDIRECT_URI&scope=whatsapp_business_management,whatsapp_business_messaging'
                }];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Get templates - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onGet('api/whatsapp/:phoneNumberId/templates')
            .reply(() => {
                // Return mock templates with the same structure as the real API
                return [200, {
                    data: [
                        {
                            name: "welcome_message",
                            message_send_ttl_seconds: 3600,
                            parameter_format: "POSITIONAL",
                            components: [
                                {
                                    type: "HEADER",
                                    format: "TEXT",
                                    text: "Bienvenido {{1}}",
                                    example: {
                                        header_text: ["Usuario"]
                                    }
                                },
                                {
                                    type: "BODY",
                                    text: "Hola {{1}}, gracias por contactarnos. ¿En qué podemos ayudarte hoy?",
                                    example: {
                                        body_text: [["Cliente"]]
                                    }
                                },
                                {
                                    type: "FOOTER",
                                    text: "Responde a este mensaje para continuar"
                                }
                            ],
                            language: "es_AR",
                            status: "APPROVED",
                            category: "UTILITY",
                            id: "1234567890",
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                        },
                        {
                            name: "order_confirmation",
                            message_send_ttl_seconds: 7200,
                            parameter_format: "POSITIONAL",
                            components: [
                                {
                                    type: "BODY",
                                    text: "Tu pedido #{{1}} ha sido confirmado y será entregado el {{2}}.",
                                    example: {
                                        body_text: [["12345", "25 de diciembre"]]
                                    }
                                },
                                {
                                    type: "BUTTONS",
                                    buttons: [
                                        {
                                            type: "URL",
                                            text: "Ver pedido",
                                            url: "https://example.com/order/{{1}}",
                                            example: ["https://example.com/order/12345"]
                                        },
                                        {
                                            type: "QUICK_REPLY",
                                            text: "Contactar soporte"
                                        }
                                    ]
                                }
                            ],
                            language: "es_MX",
                            status: "APPROVED",
                            category: "TRANSACTIONAL",
                            id: "0987654321",
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                        }
                    ],
                    paging: {
                        cursors: {
                            before: "MAZDZD",
                            after: "MjQZD"
                        }
                    }
                }];
            });

        // -----------------------------------------------------------------------------------------------------
        // @ Create template - POST
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService
            .onPost('api/whatsapp/:phoneNumberId/templates', 1000) // Simulate 1 second delay
            .reply(({ request }) => {
                const template = request.body;
                
                // Simulate creating a template
                const newTemplate = {
                    ...template,
                    id: Math.random().toString(36).substr(2, 9),
                    status: 'PENDING',
                    createdAt: new Date().toISOString(),
                    updatedAt: new Date().toISOString()
                };

                // For AUTHENTICATION templates, generate default text if not provided
                if (template.category === 'AUTHENTICATION') {
                    newTemplate.components = template.components.map((comp: any) => {
                        if (comp.type === 'BODY' && !comp.text) {
                            return {
                                ...comp,
                                text: 'Tu código de verificación es *{{1}}*. Por tu seguridad, no lo compartas.'
                            };
                        } else if (comp.type === 'FOOTER' && !comp.text && comp.code_expiration_minutes) {
                            return {
                                ...comp,
                                text: `Este código caduca en ${comp.code_expiration_minutes} minutos.`
                            };
                        }
                        return comp;
                    });
                }

                return [200, newTemplate];
            });
    }
}
